<template>
    <div class="d-flex flex-column modal-inner p-4 max-w-xxl">
        <div>
            <div class="row mx-4 pb-1">
                <h3 class="m-0">Restrict to Users</h3>
            </div>
            <div class="row mx-4 pb-1 mt-1">
                <vuetable v-if="assignedUsersLoaded"
                    ref="vuetable"
                    :api-url="grid"
                    :fields="fields"
                    :css="css.table"
                    :per-page="perPage"
                    :append-params="moreParams"
                    noDataTemplate="No Users Found"
                    pagination-path="pagination"
                    @vuetable:pagination-data="onPaginationData"
                    @vuetable:loaded="onLoaded"
                    @vuetable:checkbox-toggled="onCheckbox"
                    @vuetable:checkbox-toggled-all="onCheckboxAll"
                >
                    <template slot="name" slot-scope="props">
                        {{ props.rowData.name }}
                    </template>
                    <template slot="roles" slot-scope="props">
                        <div>
                            <a class="badge badge-primary"
                                v-for="role in props.rowData.roles"
                                :href="'/roles/' + role.id +'/edit/'"
                                :key="role.id"
                            >
                                {{ role.display_name }}
                            </a>
                        </div>
                    </template>
                    <template slot="assigned" slot-scope="props">
                        <div>
                            <span v-if="props.rowData.assigned" class="badge badge-success">
                                Yes
                            </span>
                            <span v-else class="badge badge-danger">
                                No
                            </span>
                        </div>
                    </template>
                    <template slot="actions" slot-scope="props">
                        <div class="btn-group">
                            <a class="btn btn-sm btn-outline-secondary" :href="`/users/${props.rowData.id}/edit`">
                                <i class="fa fa-eye"></i>
                            </a>
                            <button v-if="isExistingProject && props.rowData.assigned" class="btn btn-sm btn-danger" @click="handleDetachUser(props.rowData.id)" :disabled="loading">
                                <i class="fa fa-trash"></i>
                            </button>
                        </div>
                    </template>
                </vuetable>
                <div class="row d-flex justify-content-between align-items-center w-100 m-auto pb-2">
                    <span>
                        <base-pagination-info
                            :css="css.table"
                            ref="paginationInfo"
                        ></base-pagination-info>
                    </span>
                    <span>
                        <base-pagination
                            ref="pagination"
                            @vuetable-pagination:change-page="onChangePage"
                        ></base-pagination>
                    </span>
                </div>
            </div>
            <div class="row mx-4 my-0 pb-0" v-if="projectId !== ''">
                <div class="d-flex align-items-center">
                    <button v-if="isExistingProject && selectedUnassignedUsers.length" 
                        class="btn btn-sm btn-primary mr-1" 
                        @click="addUsersToRestrict" 
                        :disabled="loading"
                    >
                        Add Selected Unassigned User(s)
                    </button>
                    <button v-if="isExistingProject && selectedAssignedUsers.length" 
                        class="btn btn-sm btn-danger" 
                        @click="detachAssignedUsers" 
                        :disabled="loading"
                    >
                        Remove Selected Assigned User(s)
                    </button>
                </div>
            </div>
        </div>
        <div class="mt-4">
            <div class="row mx-4 pb-1">
                <h3 class="m-0">Limit by Role</h3>
            </div>
            <div class="row mx-4 my-1 pb-0" v-if="!loading">
                <label class="col-md-3 col-form-label font-weight-bold px-0">Assignable Roles</label>
            </div>
            <div class="row mx-4 pb-2 mt-1">
                <div class="max-w-md d-flex align-items-center">
                    <select name="" id="" class="form-control" @change="handleSelectRole" :disabled="!availableProjectRoles.length || loading">
                        <option v-for="role in availableProjectRoles" :value="role.id" :key="role.id">
                            {{ role.name }}
                        </option>
                        <option value="" v-if="availableProjectRoles.length === 0">
                            No roles available
                        </option>
                    </select>
                    <button class="btn btn-primary ml-1" @click="handleAddRole" :disabled="!availableProjectRoles.length || loading || pendingProjectRoleIds.includes(selectedRole.id)">
                        Add Role
                    </button>
                </div>
            </div>
            <div class="row mx-4 my-1 pb-0" v-if="!loading">
                <label class="col-md-3 col-form-label font-weight-bold px-0">Attached/Pending Roles</label>
            </div>
            <div class="row mx-4 my-0 pb-0 d-flex flex-column">
                <div v-if="isExistingProject" class="d-flex flex-column max-w-md">
                    <div v-for="role in assignedProjectRoles" :key="role.id" class="d-flex w-100 align-items-center justify-content-between mb-2">   
                        <span>{{ role.name }}</span>
                        <button class="btn btn-sm btn-danger" @click="handleDetachRole(role.id)" :disabled="loading">
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>
                    <div v-if="!assignedProjectRoles.length">No Roles Attached</div>
                </div>
                <div v-if="!isExistingProject" class="d-flex flex-column max-w-md">
                    <div v-for="role in pendingProjectRoles" :key="role.id" class="d-flex w-100 align-items-center justify-content-between mb-2">   
                        <span>{{ role.name }}</span>
                        <button class="btn btn-sm btn-danger" @click="handleDetachRole(role.id)" :disabled="loading">
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>
                    <div v-if="!pendingProjectRoles.length">No Roles Attached</div>
                </div>
                <div class="d-flex align-items-center mt-2">
                    <button class="btn my-2 mr-2 btn-outline-secondary" @click="handleClose">
                        Cancel
                    </button>
                    <button v-if="!isExistingProject" class="btn btn-primary" @click="handleSave">
                        Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import axios from "axios";
import SelectItemFromAPIprovidedList from "../../generic/select-item-from-api-provided-list.vue";
import BasePagination from "../../tables/base-pagination.vue";
import BasePaginationInfo from "../../tables/base-pagination-info.vue";


const base_url = window.location.protocol + "//" + window.location.host;

export default {
    name: "project-restrict-user",
    components: {
        "selection-lister": SelectItemFromAPIprovidedList,
        "base-pagination": BasePagination,
        "base-pagination-info": BasePaginationInfo,
    },
    props: {
        isProject: {
            type: [Boolean],
            default: false
        },
        projectId: {
            type: [Number, String],
            required: false
        },
        pendingUsers: {
            type: [Array, Object],
            required: false
        },
        pendingRoles: {
            type: [Array, Object],
            required: false
        }
    },
    data() {
        return {
            assignedUsers: [],
            assignedUsersLoaded: false,
            assignedProjectRoles: [],
            availableProjectRoles: [],
            pendingProjectRoles: [],
            selectedRole: {
                id: null,
                name: ''
            },
            selectedProjectUsers: this.pendingUsers.length ? this.pendingUsers : [],
            pendingProjectUsers: [],
            perPage: 4,
            moreParams: {},
            loading: true,
            fields: [
                {
                    name: "__checkbox",
                    titleClass: 'center aligned',
                    dataClass: 'right aligned'
                },
                {
                    name: "__slot:name",
                    sortField: "firstname",
                    title: "Full Name",
                },
                {
                    name: "email",
                    title: "Email Address",
                },
                {
                    name: "__slot:roles",
                    title: "Roles",
                },
                {
                    name: "__slot:actions",
                    titleClass: "actions-single-column",
                },
            ],
            grid: base_url + "/users/grid",
            css: {
                table: {
                    tableClass:
                        "table table-striped table-bordered table-middle",
                    ascendingIcon: "sort-icon-asc",
                    descendingIcon: "sort-icon-desc",
                    infoClass: "",
                },
            },
        }
    },
    computed: {
        isExistingProject() {
            return this.projectId !== "" && !isNaN(parseInt(this.projectId));
        },
        selectedProjectUserIds() {
            return this.selectedProjectUsers.map((user) => user.id);
        },
        selectedAssignedUsers() {
            return this.selectedProjectUsers.filter((user) => user.assigned).map((user) => user.id);
        },
        selectedUnassignedUsers() {
            return this.selectedProjectUsers.filter((user) => !user.assigned).map((user) => user.id);
        },
        pendingProjectRoleIds() {
            return this.pendingProjectRoles.map((project) => project.id);
        },
    },
    watch: {
        availableProjectRoles: function() {
            if (this.availableProjectRoles.length === 0) {
                this.selectedRole = { name: '', id: null }
            } else {
                this.selectedRole = this.availableProjectRoles[0];
            }
        },
        selectedProjectUsers: function() {
            this.pendingProjectUsers = this.selectedProjectUsers;
        }
    },
    mounted() {
        this.getAvailableRoles();

        if (this.isExistingProject) {
            const users = `${base_url}/pimapi/projects/${this.projectId}/users`;
            const roles = `${base_url}/pimapi/projects/${this.projectId}/roles`;

            const getUsers = axios.get(users);
            const getRoles = axios.get(roles);

            axios.all([getUsers, getRoles])
                .then(axios.spread((...response) => {
                    let assignedUsers = response[0].data.payload;
                    let assignedRoles = response[1].data.payload;

                    let assignedRolesIds = [];

                    assignedRoles = assignedRoles.reduce((a, c) => {
                        if (!assignedRolesIds.includes(c.id)) {
                            assignedRolesIds.push(c.id);
                            c.name = c.display_name;
                            a.push(c);
                        }

                        return a;
                    }, []);


                    this.assignedUsers = assignedUsers.map((user) => user.id);
                    this.assignedUsersLoaded = true;

                    this.assignedProjectRoles = assignedRoles;
                    
                    this.fields.splice(this.fields.length - 1, 0, {
                        name: "__slot:assigned",
                        title: "Assigned",
                    });

                    this.$nextTick(() => {
                        this.$refs.vuetable.normalizeFields();
                    });
                }))
                .catch((errors) => {
                    console.log(errors);
                });
        } else {
            this.assignedUsersLoaded = true;
        }
    },
    methods: {
        async getAvailableRoles() {
            if (this.isExistingProject) {
                const getAllRoles = axios.get(`${base_url}/pimapi/roles`);
                const getProjectRoles = axios.get(`${base_url}/pimapi/projects/${this.projectId}/roles`);

                axios.all([getAllRoles, getProjectRoles])
                    .then(axios.spread((...response) => {
                        const allRoles = response[0].data.roles;
                        const projectRoles = response[1].data.payload;

                        const projectRolesFormatted = projectRoles.reduce((a, c) => {
                            c.name = c.display_name;
                            a.push(c);

                            return a;
                        }, [])

                        const projectRoleIds = projectRoles.map((role) => role.id);
                        
                        this.availableProjectRoles = allRoles.filter((role) => !projectRoleIds.includes(role.id));
                        this.assignedProjectRoles = projectRolesFormatted;
                    }));
            } else {
                axios(base_url + '/pimapi/roles')
                    .then((response) => {
                        this.availableProjectRoles = response.data.roles;
                    })
                    .catch((err) => {
                        console.log(err)
                    });
            }
        },
        async getAssignedUsers() {
            await axios(base_url + '/pimapi/projects/' + this.projectId + '/users')
                .then((response) => {
                    this.assignedUsers = response.data.payload.map((user) => user.id);
                    this.assignedUsersLoaded = true;
                })
                .catch((err) => {
                        this.handleAlert({
                            message: 'Error fetching project users',
                            status: "alert-danger"
                        });
                })
        },  
        preselectCheckboxes() {
            if (!this.isExistingProject) {
                if (this.pendingUsers.length) {
                    this.$refs.vuetable.selectedTo = this.pendingUsers.map((user) => user.id);
                }

                if (this.pendingRoles.length) {
                    this.pendingProjectRoles = this.pendingRoles;
                }
            }
        },
        onPaginationData(paginationData) {
            this.$refs.pagination.setPaginationData(paginationData);
            this.$refs.paginationInfo.setPaginationData(paginationData);
            this.preselectCheckboxes();
        },
        onChangePage(page) {
            this.$refs.vuetable.changePage(page);
            this.preselectCheckboxes();
        },
        onLoaded: function () {
            this.loading = false;
        
            this.preselectCheckboxes();
        },
        onCheckbox(isChecked, row) {
            if (isChecked) {
                this.selectedProjectUsers.push(row);
            } else {
                this.selectedProjectUsers = 
                    this.selectedProjectUsers.filter((pendingUser) => pendingUser.id !== row.id);
            }
        },
        onCheckboxAll(isChecked) {
            if (isChecked === false) {
                this.$refs.vuetable.selectedTo = [];
                this.selectedProjectUsers = [];
            } else {
                let selected = this.$refs.vuetable.tableData.map(item => item);
                this.selectedProjectUsers = this.selectedProjectUsers.concat(selected);
            }
        },
        handleClose() {
            this.$parent.restrictUserModal = false;
        },
        handleAlert(alert) {
            this.$parent.$parent.alert = alert;
        },
        handleSelectRole(e) {
            this.availableProjectRoles.forEach((role) => {
                if (role.id == e.target.value) {
                    this.selectedRole = role;
                }
            })
        },
        handleDetachRole(id) {            
            if (this.isExistingProject) {
                this.loading = true;

                axios.delete(base_url + '/pimapi/projects/' + this.projectId + '/roles/detach', { data: { roleIds: [id] }})
                    .then(function(res) {
                        this.loading = false;
                        
                        this.handleAlert({
                            message: 'User role removed',
                            status: "alert-success"
                        });

                        this.getAvailableRoles();
                        this.$refs.vuetable.refresh();
                    }.bind(this))
                    .catch(function(err) {
                        this.loading = false;
                        
                        this.handleAlert({
                            message: 'An error occurred',
                            status: "alert-success"
                        });

                        window.setTimeout(() => {
                            window.location.reload();
                        }, 3500)
                    }.bind(this));
            } else {
                this.removePendingRole(id);
            }
        },
        handleAddRole() {
            if (this.isExistingProject) {
                this.loading = true;

                axios.post(base_url + '/pimapi/projects/' + this.projectId + '/roles', {
                    roleIds: [this.selectedRole.id],
                })
                    .then(function(res) {
                        this.loading = false;
                        
                        this.handleAlert({
                            message: 'User role added',
                            status: "alert-success"
                        });

                        this.getAvailableRoles();
                        this.$refs.vuetable.refresh();
                    }.bind(this))
                    .catch(function(err) {
                        this.loading = false;
                        
                        this.handleAlert({
                            message: 'An error occurred',
                            status: "alert-success"
                        });
                    }.bind(this));
            } else {
                this.addPendingRole()
            }
        },
        addPendingRole() {
            this.pendingProjectRoles.push(this.selectedRole);
            this.availableProjectRoles = this.availableProjectRoles.filter((role) => role.id !== this.selectedRole.id);
        },
        removePendingRole(id) {
            let currentRole = this.pendingProjectRoles.find((pendingRole) => pendingRole.id == id);
            this.availableProjectRoles.push(currentRole);
            this.pendingProjectRoles = this.pendingProjectRoles.filter((pendingRole) => pendingRole.id != id);
        },
        async addUsersToRestrict() {
            this.loading = true;

            await axios
                .post("/pimapi/projects/" + this.projectId + "/users", {
                    userIds: this.selectedUnassignedUsers
                })
                .then((response) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-success",
                        message: 'User(s) added',
                    });
                })
                .catch((err) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-danger",
                        message: err.response.data.message,
                    });
                })

            await this.getAssignedUsers();
            this.clearTable();
        },
        async detachAssignedUsers() {
            this.loading = true;

            await axios
                .post("/pimapi/projects/" + this.projectId + "/users/detach", {
                    userIds: this.selectedAssignedUsers
                })
                .then((response) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-success",
                        message: "User removed",
                    });
                })
                .catch((err) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-danger",
                        message: err.response.data.message,
                    });
                })

            await this.getAssignedUsers();
            this.clearTable();
        },
        async handleDetachUser(id) {
            this.loading = true;

            axios
                .post("/pimapi/projects/" + this.projectId + "/users/detach", {
                    userIds: [id]
                })
                .then((response) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-success",
                        message: "User removed",
                    });

                    this.$refs.vuetable.refresh();
                })
                .catch((err) => {
                    this.loading = false;

                    this.handleAlert({
                        status: "alert-danger",
                        message: err.response.data.message,
                    });
                })

            await this.getAssignedUsers();
            this.clearTable();
        },
        clearTable() {
            this.selectedProjectUsers = [];
            this.$refs.vuetable.selectedTo = [];
            this.$refs.vuetable.refresh();
        },
        handleSave() {
            this.$parent.pendingProjectRoles = this.pendingProjectRoles;
            this.$parent.pendingProjectUsers = this.pendingProjectUsers;
            this.$parent.availableProjectRoles = this.availableProjectRoles;
            this.handleClose();
        },
        transform(data) {
            const transformed = {};

            transformed.pagination = {
                total: data.total,
                per_page: data.per_page,
                current_page: data.current_page,
                last_page: data.last_page,
                next_page_url: data.next_page_url,
                prev_page_url: data.prev_page_url,
                from: data.from,
                to: data.to
            }

            transformed.data = [];

            for (let i = 0; i < data.data.length; i++) {
                transformed.data.push({
                    id: data.data[i].id,
                    email: data.data[i].email,
                    name: data.data[i].name,
                    roles: data.data[i].roles,
                    assigned: this.assignedUsers.includes(data.data[i].id)
                });
            }

            transformed.data.sort((a, b) => a.name.localeCompare(b.name));

            return transformed
        }
    }
}
</script> 
